/**
 * GA4 / Meta Pixel / Google Ads dataLayer pushes.
 *
 * Runs alongside the existing middleware analytics (src/lib/booking/analytics.ts).
 * This module ONLY pushes to window.dataLayer for tag-manager consumption —
 * it never affects booking logic or the middleware event stream.
 */

import type { CartItem, AvailabilityCategory } from "@/lib/booking/types";

type Ecommerce = {
  currency?: string;
  value?: number;
  coupon?: string;
  promotion_id?: string;
  promotion_name?: string;
  items: EcommerceItem[];
};

export type EcommerceItem = {
  item_id: string;
  item_name: string;
  item_category?: string;
  price: number;
  quantity: number;
};

function dl(): Record<string, unknown>[] {
  if (typeof window === "undefined") return [];
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
}

export function pushEcommerce(eventName: string, ecommerce: Ecommerce): void {
  try {
    const layer = dl();
    layer.push({ ecommerce: null }); // reset so item data never leaks event-to-event
    layer.push({ event: eventName, ecommerce });
  } catch {
    // never let analytics break the page
  }
}

/**
 * Persist the current cart items array to sessionStorage so the
 * /srcthankyou page can rebuild a GA4 `purchase` event after the
 * Checkfront payment round-trip (URL only carries booking_id + total).
 */
export function persistCartItems(items: EcommerceItem[]): void {
  try {
    if (typeof sessionStorage === "undefined") return;
    sessionStorage.setItem("srr_cart_items", JSON.stringify(items ?? []));
  } catch {
    /* noop */
  }
}

export function pushSearch(searchTerm: string): void {
  try {
    dl().push({ event: "search", search_term: searchTerm });
  } catch {
    /* noop */
  }
}

export function pushAttribution(attribution: Record<string, string | null | undefined>): void {
  try {
    // Strip null/undefined/empty for a cleaner dataLayer.
    const clean: Record<string, string> = {};
    for (const [k, v] of Object.entries(attribution)) {
      if (v) clean[k] = String(v);
    }
    dl().push({ attribution: clean });
  } catch {
    /* noop */
  }
}

/* ------------ Item adapters ------------ */

export function cartItemToItem(ci: CartItem): EcommerceItem {
  const qty = Number(ci.qty ?? 1) || 1;
  return {
    item_id: ci.type,
    item_name: ci.unitName || ci.label || ci.type,
    item_category: "overnight",
    price: Number(ci.pricePerNight) || 0,
    quantity: qty,
  };
}

export function availabilityCategoryToItem(cat: AvailabilityCategory): EcommerceItem {
  return {
    item_id: cat.type,
    item_name: cat.label,
    item_category: cat.overnight ? "overnight" : "day",
    price: Number(cat.pricing?.lowest) || 0,
    quantity: 1,
  };
}

/** Build an EcommerceItem from a loose shape (used at sites where we only have
 *  a category + price, not a full CartItem). */
export function makeItem(args: {
  id: string;
  name?: string;
  category?: string;
  price?: number | string | null;
  quantity?: number;
}): EcommerceItem {
  return {
    item_id: args.id,
    item_name: args.name || args.id,
    item_category: args.category,
    price: Number(args.price ?? 0) || 0,
    quantity: Number(args.quantity ?? 1) || 1,
  };
}

/* ------------ User data (hashed) ------------ */

async function sha256(value: string): Promise<string> {
  if (!value || typeof crypto === "undefined" || !crypto.subtle) return "";
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function toE164Digits(phone: string): string {
  if (!phone) return "";
  const trimmed = phone.trim();
  const digits = trimmed.replace(/[^\d]/g, "");
  if (!digits) return "";
  if (trimmed.startsWith("+")) return `+${digits}`;
  // Assume US if 10-digit, else prepend +.
  if (digits.length === 10) return `+1${digits}`;
  return `+${digits}`;
}

export async function pushUserData(opts: {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
}): Promise<void> {
  try {
    const email = (opts.email ?? "").trim().toLowerCase();
    const fn = (opts.firstName ?? "").trim().toLowerCase();
    const ln = (opts.lastName ?? "").trim().toLowerCase();
    const ph = toE164Digits(opts.phone ?? "");

    const user_data: Record<string, string> = {};
    if (email) user_data.em = await sha256(email);
    if (ph) user_data.ph = await sha256(ph);
    if (fn) user_data.fn = await sha256(fn);
    if (ln) user_data.ln = await sha256(ln);

    if (Object.keys(user_data).length === 0) return;
    // Persist for the post-Checkfront /srcthankyou page so the `purchase`
    // event can re-emit user_data (DLVs are otherwise undefined after the
    // cross-origin payment round-trip).
    try {
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem("srr_user_data", JSON.stringify(user_data));
      }
    } catch { /* noop */ }
    dl().push({ event: "user_data_available", user_data });
  } catch {
    /* noop */
  }
}
